import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useMediaQuery } from 'react-responsive';

import {
  filterObjectType,
  filtersHookType,
  resourcesType,
} from '../../../types';
import { LoginTypeEnum } from 'modules/utils/types';
import { useSelectedFilters } from './hooks/useSelectedFilters';
import * as Button from 'components/Button';
import * as Filters from '../../Filters';

import styles from './styles.module.scss';
import { SortByTypes } from 'pages/resource-hub/utils';
import { ALL_FILTERS_ELEMENT } from 'modules/utils/constants';
import {
  CategoryList,
  filterCategoryType,
  filterResourceType,
} from 'pages/resource-hub/helper';

type FiltersContainerProps = {
  filters: filtersHookType;
  getData: (resetNumberOfitemsShown: boolean) => Promise<void>;
  getSortByValue: (value: SortByTypes) => void;
  loginTypeContext: LoginTypeEnum;
  resources: resourcesType;
  slug: string;
  searchInitiated: boolean;
  searchTerm: string;
};

export const FiltersContainer: React.FunctionComponent<FiltersContainerProps> =
  ({
    filters,
    getData,
    getSortByValue,
    loginTypeContext,
    resources,
    slug = '',
    searchInitiated,
    searchTerm,
  }) => {
    const isResourceHub = window.location.href.includes('/resource-hub')
      ? true
      : false;
    const [isCategorySelected, setIsCategorySelected] = useState(false);
    const [isResourceTypeSelected, setIsResourceTypeSelected] = useState(false);
    const [isCertificateSelected, setIsCertificateSelected] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const wrapperRef = useRef(null);

    const defaultResourceType = {
      data: [
        {
          uid: '',
          colour: '',
          title: '',
          slug: '',
        },
      ],
    };
    const [newResourceTypes, setNewResourceTypes] =
      useState(defaultResourceType);
    const [newCategoryTypes, setNewCategoryTypes] =
      useState(defaultResourceType);

    const defaultResourcesForEvents = {
      data: [
        ALL_FILTERS_ELEMENT,
        {
          uid: 'bltce636d390f3194a5',
          colour: '',
          title: 'Podcasts',
          slug: 'podcasts',
        },
        {
          uid: 'bltbfe6436ac6a934ac',
          colour: '',
          title: 'Webinars',
          slug: 'webinars',
        },
      ],
    };

    const isMobile = useMediaQuery({ query: '(max-width: 520px)' });

    const closeFilters = () => {
      setIsCategorySelected(false);
      setIsResourceTypeSelected(false);
      setIsCertificateSelected(false);
    };
    const {
      addDeleteResourceType,
      addDeleteCertificate,
      addDeleteCategory,
      refreshResults,
      setRefreshResults,
    } = useSelectedFilters(filters, loginTypeContext, resources, slug);

    const classNames = clsx(
      styles.filtersContainer,
      isMobile && showFilters && styles.showFilters,
    );

    const getSortByType = (value: any) => {
      getSortByValue(value);
    };

    useEffect(() => {
      if (refreshResults) {
        getData(true);
        setRefreshResults(false);
      }
    }, [refreshResults, setRefreshResults, getData]);

    useEffect(() => {
      const resourceType = filterResourceType(filters.contentTypesSelected);
      setNewResourceTypes(resourceType);
    }, [filters.contentTypesSelected]);

    useEffect(() => {
      const categoryTypes = filterCategoryType(filters.topicsSelected);
      setNewCategoryTypes(categoryTypes);
    }, [filters.topicsSelected]);

    function revertSlug(slug: string) {
      if (!slug || slug.trim() === '') return '';

      // Convert dashes to spaces
      let reverted = slug.replace(/-/g, ' ');

      // Replace 'and' with '&' (whole word only, case-insensitive)
      reverted = reverted.replace(/\band\b/gi, '&');

      // Capitalize the first letter of each word
      const words = reverted.split(' ').filter(Boolean);

      const capitalized = words.map(
        (word: string) => word.charAt(0).toUpperCase() + word.slice(1),
      );

      return capitalized.join(' ');
    }

    const getFilterObjectsFromLocalStorage = (
      setContentTypesSlug: string,
    ): filterObjectType[] => {
      const stored = localStorage.getItem(setContentTypesSlug);
      if (!stored)
        return [
          {
            colour: '',
            slug: 'All',
            title: 'All',
            uid: '',
          },
        ];

      const titles = stored.split(',').map(t => t.trim());

      const filterObjects: filterObjectType[] = titles.map((title, index) => ({
        title: revertSlug(title),
        slug: revertSlug(title),
        uid: '',
        colour: '', // cycle through colors
      }));

      return filterObjects;
    };

    const localStorageCT = getFilterObjectsFromLocalStorage(
      'setContentTypesSlug',
    );

    const localStorageBP = getFilterObjectsFromLocalStorage(
      'setBestPracticesSlug',
    );

    const localStorageTopics =
      getFilterObjectsFromLocalStorage('setTopicsSlug');

    return (
      <div className={classNames} ref={wrapperRef}>
        <div>
          <Filters.Filter
            addDeleteItems={addDeleteCategory}
            closeFilters={closeFilters}
            isFilterSelected={isCategorySelected}
            itemsSelected={localStorageCT}
            listItems={
              isResourceHub ? newCategoryTypes : resources.contentTypes
            }
            setIsFilterSelected={setIsCategorySelected}
            setItemsSelected={filters.setContentTypesSelected}
            title="Category"
          />
          <Filters.Filter
            addDeleteItems={addDeleteResourceType}
            closeFilters={closeFilters}
            isFilterSelected={isResourceTypeSelected}
            itemsSelected={localStorageTopics}
            listItems={
              isResourceHub ? newResourceTypes : defaultResourcesForEvents
            }
            setIsFilterSelected={setIsResourceTypeSelected}
            setItemsSelected={filters.setTopicsSelected}
            title="Resource Type"
          />
          <Filters.Filter
            addDeleteItems={addDeleteCertificate}
            closeFilters={closeFilters}
            isFilterSelected={isCertificateSelected}
            itemsSelected={localStorageBP}
            listItems={resources.bestPractices}
            setIsFilterSelected={setIsCertificateSelected}
            setItemsSelected={filters.setBestPracticesSelected}
            title="Certificate"
          />

          {(localStorageBP.filter(ct => ct.title !== 'All')[0] ||
            localStorageTopics.filter(ct => ct.title !== 'All')[0] ||
            localStorageCT.filter(ct => ct.title !== 'All')[0] ||
            (searchTerm !== '' && searchInitiated)) && (
            <Filters.SortBy getSortByType={getSortByType} />
          )}

          {isMobile && (
            <Button.Generic
              className={styles.showFiltersButton}
              label="More filters"
              onClick={() => setShowFilters(true)}
            />
          )}
        </div>
        {(localStorageBP.filter(ct => ct.title !== 'All')[0] ||
          localStorageTopics.filter(ct => ct.title !== 'All')[0] ||
          localStorageCT.filter(ct => ct.title !== 'All')[0]) && (
          <div>
            <Filters.Selected
              itemsSelected={localStorageCT}
              addDeleteItems={addDeleteCategory}
            />
            <Filters.Selected
              itemsSelected={localStorageTopics}
              addDeleteItems={addDeleteResourceType}
            />
            <Filters.Selected
              itemsSelected={localStorageBP}
              addDeleteItems={addDeleteCertificate}
            />
          </div>
        )}
      </div>
    );
  };

export default FiltersContainer;
